// The text field style shared by the Flutter auth screens: black @70% fill,
// 20px rounded outline in #01111F, FontColor text/hint, and a leading icon.
//
// ── Why `numeric` and `mask` exist ─────────────────────────────────────────
//
// Every field on these screens is digits — a 10-digit mobile, a 4-digit PIN, a
// 4-digit OTP — and both of the obvious ways to say so were silently failing.
//
// 1. ⚠️ `inputMode` PASSED AS A PROP NEVER REACHED THE INPUT. MUI's TextField
//    forwards props it does not recognise to the root FormControl, not to the
//    <input>, so `<AuthField inputMode="numeric" />` set the attribute on a
//    wrapper <div> where no keyboard will ever look at it. Every screen here
//    passed it that way and every one got the full alphanumeric keyboard. It
//    has to go inside `inputProps` to land on the element.
//
// 2. ⚠️ `type="password"` OVERRIDES inputMode ON ANDROID. The IME picks its
//    layout from the input TYPE, and a password input maps to the text-password
//    layout whatever `inputmode` says. So a masked PIN cannot ask for a numeric
//    keypad and stay `type="password"` — the two requirements are in direct
//    conflict.
//
//    `mask` resolves it: the field stays `type="text"` (so the keypad is
//    numeric) and the characters are hidden with `-webkit-text-security`, which
//    both the Android WebView and iOS WKWebView support. Autofill and
//    autocorrect are turned off alongside, since a text input would otherwise
//    offer suggestions for something that is not a word.

import React from 'react';
import { TextField, InputAdornment, useTheme } from '@mui/material';

export default function AuthField({
  icon,
  endAdornment,
  maxLength,
  numeric,          // digits-only keypad
  mask,             // hide the characters WITHOUT type="password"
  sx,
  inputProps,
  ...props
}) {
  const theme = useTheme();
  const c = theme.sli;

  // `pattern` is what iOS reads for the digits-only pad; `inputMode` is what
  // Android reads. Both, or one of the two platforms shows letters.
  const mergedInputProps = {
    ...(maxLength ? { maxLength } : null),
    ...(numeric
      ? {
        inputMode: 'numeric',
        pattern: '[0-9]*',
        autoComplete: 'off',
        autoCorrect: 'off',
        autoCapitalize: 'none',
        spellCheck: false,
      }
      : null),
    ...inputProps,
  };

  return (
    <TextField
      fullWidth
      variant="outlined"
      inputProps={mergedInputProps}
      InputProps={{
        startAdornment: icon
          ? <InputAdornment position="start" sx={{ color: c.text }}>{icon}</InputAdornment>
          : undefined,
        endAdornment,
        sx: {
          bgcolor: 'rgba(0,0,0,0.7)',
          borderRadius: '20px',
          color: c.text,
          fontSize: 15,
          '& fieldset': { borderColor: '#01111F', borderWidth: 2 },
          '&:hover fieldset': { borderColor: '#01111F' },
          '&.Mui-focused fieldset': { borderColor: '#01111F' },
          '& input::placeholder': { color: c.text, opacity: 1 },
          // Masking without type="password" — see the note at the top.
          ...(mask ? { '& input': { WebkitTextSecurity: 'disc' } } : null),
        },
      }}
      sx={sx}
      {...props}
    />
  );
}
